import { useMemo, useState } from "react";
import {
  activityLabel,
  cmToIn,
  completeOnboarding,
  computeBMR,
  computeTDEE,
  goalLabel,
  inToCm,
  kgToLbs,
  lbsToKg,
  recommendedCalories,
  recommendedMacros,
  type ActivityLevel,
  type Goal,
  type Sex
} from "../lib/onboarding";
import type { WeightUnit } from "../lib/weight";
import { friendlyError } from "../lib/errors";

interface Props {
  onComplete: () => void;
  onSkip: () => void;
}

type Step = "welcome" | "basics" | "activity" | "goal" | "review";

const ACTIVITY_OPTIONS: { value: ActivityLevel; emoji: string }[] = [
  { value: "sedentary", emoji: "🛋️" },
  { value: "light", emoji: "🚶" },
  { value: "moderate", emoji: "🚴" },
  { value: "very", emoji: "🏋️" },
  { value: "extreme", emoji: "🏃" }
];

const GOAL_OPTIONS: { value: Goal; emoji: string }[] = [
  { value: "lose", emoji: "📉" },
  { value: "maintain", emoji: "➡️" },
  { value: "gain", emoji: "📈" }
];

export default function Onboarding({ onComplete, onSkip }: Props) {
  const [step, setStep] = useState<Step>("welcome");

  // Basics
  const [sex, setSex] = useState<Sex>("male");
  const [age, setAge] = useState(35);
  const [unit, setUnit] = useState<WeightUnit>("lbs");

  // Height tracked separately per unit so toggling doesn't round-trip data.
  const [heightFt, setHeightFt] = useState(5);
  const [heightIn, setHeightIn] = useState(10);
  const [heightCmState, setHeightCmState] = useState(178);

  // Weight is free-typed (string buffer) so the user can clear/edit without
  // every keystroke being clamped or coerced to 0.
  const [weightInput, setWeightInput] = useState("170");

  const heightCm = unit === "kg" ? heightCmState : inToCm(heightFt * 12 + heightIn);
  const weightNum = parseFloat(weightInput);
  const safeWeightNum = Number.isFinite(weightNum) ? weightNum : 0;
  const weightKg = unit === "kg" ? safeWeightNum : lbsToKg(safeWeightNum);

  const [activity, setActivity] = useState<ActivityLevel>("moderate");
  const [goal, setGoal] = useState<Goal>("maintain");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bmr = useMemo(() => computeBMR(sex, weightKg, heightCm, age), [sex, weightKg, heightCm, age]);
  const tdee = useMemo(() => computeTDEE(bmr, activity), [bmr, activity]);
  const targetCalories = useMemo(() => recommendedCalories(tdee, goal), [tdee, goal]);
  const macros = useMemo(() => recommendedMacros(targetCalories), [targetCalories]);

  async function save() {
    if (!Number.isFinite(weightNum) || weightNum <= 0) {
      setError("Enter your current weight.");
      setStep("basics");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await completeOnboarding({
        sex,
        age,
        weight_kg: Math.round(weightKg * 10) / 10,
        height_cm: Math.round(heightCm * 10) / 10,
        activity,
        goal,
        unit,
        goals: {
          calories: targetCalories,
          ...macros
        }
      });
      onComplete();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  function changeUnit(next: WeightUnit) {
    if (next === unit) return;
    const current = parseFloat(weightInput);
    if (next === "kg") {
      // Convert ft+in → cm (keep as integer cm for the select)
      setHeightCmState(Math.round(inToCm(heightFt * 12 + heightIn)));
      // Convert lbs string → kg, round to 1 decimal
      if (Number.isFinite(current) && current > 0) {
        setWeightInput((Math.round(lbsToKg(current) * 10) / 10).toString());
      }
    } else {
      // Convert cm → ft + in
      const totalIn = cmToIn(heightCmState);
      const ft = Math.max(3, Math.min(7, Math.floor(totalIn / 12)));
      const inches = Math.max(0, Math.min(11, Math.round(totalIn - ft * 12)));
      setHeightFt(ft);
      setHeightIn(inches);
      // Convert kg string → lbs, round to integer
      if (Number.isFinite(current) && current > 0) {
        setWeightInput(Math.round(kgToLbs(current)).toString());
      }
    }
    setUnit(next);
  }

  const stepIndex = ["welcome", "basics", "activity", "goal", "review"].indexOf(step);

  return (
    <div className="min-h-full flex flex-col max-w-md mx-auto">
      {/* Progress */}
      <div className="px-4 pt-6">
        <div className="flex gap-1.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className={`flex-1 h-1 rounded-full ${
                i <= stepIndex ? "bg-emerald-500" : "bg-slate-800"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="flex-1 px-4 py-6 space-y-6">
        {step === "welcome" && (
          <div className="space-y-4">
            <div className="text-5xl">👋</div>
            <h1 className="text-3xl font-bold text-slate-100">Let's personalize this</h1>
            <p className="text-slate-400">
              60 seconds, 4 questions. We'll compute your maintenance calories and set sensible
              targets so your dashboard means something on day one.
            </p>
            <p className="text-xs text-slate-500">
              Everything is stored privately in your account. You can change these anytime in Settings.
            </p>
          </div>
        )}

        {step === "basics" && (
          <div className="space-y-5">
            <h2 className="text-2xl font-bold text-slate-100">A few basics</h2>

            <Field label="Sex">
              <div className="grid grid-cols-2 gap-2">
                {(["male", "female"] as Sex[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSex(s)}
                    className={`py-3 rounded-lg border ${
                      sex === s
                        ? "bg-emerald-950 border-emerald-700 text-emerald-100"
                        : "bg-slate-900 border-slate-800 text-slate-300"
                    }`}
                  >
                    {s === "male" ? "Male" : "Female"}
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-slate-400 mt-1">
                Used only for the BMR formula. Pick whichever maps to your biology.
              </div>
            </Field>

            <Field label="Units">
              <div className="grid grid-cols-2 gap-2">
                {(["lbs", "kg"] as WeightUnit[]).map((u) => (
                  <button
                    key={u}
                    onClick={() => changeUnit(u)}
                    className={`py-3 rounded-lg border ${
                      unit === u
                        ? "bg-emerald-950 border-emerald-700 text-emerald-100"
                        : "bg-slate-900 border-slate-800 text-slate-300"
                    }`}
                  >
                    {u === "lbs" ? "Lbs + inches" : "Kg + cm"}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Age">
              <select
                value={age}
                onChange={(e) => setAge(parseInt(e.target.value, 10))}
                className="w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 focus:outline-none focus:border-emerald-500 appearance-none"
              >
                {Array.from({ length: 88 }, (_, i) => i + 13).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </Field>

            <Field label="Height">
              {unit === "lbs" ? (
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={heightFt}
                    onChange={(e) => setHeightFt(parseInt(e.target.value, 10))}
                    className="w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 focus:outline-none focus:border-emerald-500 appearance-none"
                  >
                    {[3, 4, 5, 6, 7].map((ft) => (
                      <option key={ft} value={ft}>{ft} ft</option>
                    ))}
                  </select>
                  <select
                    value={heightIn}
                    onChange={(e) => setHeightIn(parseInt(e.target.value, 10))}
                    className="w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 focus:outline-none focus:border-emerald-500 appearance-none"
                  >
                    {Array.from({ length: 12 }, (_, i) => i).map((inch) => (
                      <option key={inch} value={inch}>{inch} in</option>
                    ))}
                  </select>
                </div>
              ) : (
                <select
                  value={heightCmState}
                  onChange={(e) => setHeightCmState(parseInt(e.target.value, 10))}
                  className="w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 focus:outline-none focus:border-emerald-500 appearance-none"
                >
                  {Array.from({ length: 141 }, (_, i) => i + 100).map((cm) => (
                    <option key={cm} value={cm}>{cm} cm</option>
                  ))}
                </select>
              )}
            </Field>

            <Field label={`Current weight (${unit})`}>
              <input
                type="text"
                inputMode="decimal"
                value={weightInput}
                onChange={(e) => {
                  // Allow free typing — only digits + one decimal point
                  const cleaned = e.target.value.replace(/[^0-9.]/g, "");
                  // Keep just the first decimal
                  const parts = cleaned.split(".");
                  const final = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : cleaned;
                  setWeightInput(final);
                }}
                placeholder={unit === "kg" ? "75" : "170"}
                className="w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
              />
              <div className="text-[11px] text-slate-400 mt-1">
                Saved as today's weight entry — start tracking your trend from here.
              </div>
            </Field>
          </div>
        )}

        {step === "activity" && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-100">How active are you, typically?</h2>
            <p className="text-sm text-slate-400">Pick the closest match — exercise + general lifestyle.</p>
            <div className="space-y-2">
              {ACTIVITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setActivity(opt.value)}
                  className={`w-full p-3 rounded-lg border text-left flex items-center gap-3 ${
                    activity === opt.value
                      ? "bg-emerald-950 border-emerald-700"
                      : "bg-slate-900 border-slate-800"
                  }`}
                >
                  <span className="text-2xl">{opt.emoji}</span>
                  <span className="text-sm text-slate-200">{activityLabel(opt.value)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "goal" && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-100">What's your goal?</h2>
            <p className="text-sm text-slate-400">We'll set your daily calorie target accordingly.</p>
            <div className="space-y-2">
              {GOAL_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setGoal(opt.value)}
                  className={`w-full p-3 rounded-lg border text-left flex items-center gap-3 ${
                    goal === opt.value
                      ? "bg-emerald-950 border-emerald-700"
                      : "bg-slate-900 border-slate-800"
                  }`}
                >
                  <span className="text-2xl">{opt.emoji}</span>
                  <span className="text-sm text-slate-200">{goalLabel(opt.value)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-100">Your numbers</h2>

            <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
              <div className="text-xs text-slate-400 uppercase tracking-wide">Maintenance calories</div>
              <div className="text-3xl font-bold text-slate-100 tabular-nums">
                {Math.round(tdee)} <span className="text-sm font-normal text-slate-500">kcal/day</span>
              </div>
              <div className="text-xs text-slate-500">
                What you'd eat to keep your weight steady — sometimes called your TDEE.
              </div>
              <div className="text-[11px] text-slate-600">
                Calculated from your stats: BMR {Math.round(bmr)} × activity {ACTIVITY_OPTIONS.find((a) => a.value === activity)?.emoji}
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-emerald-950 border border-emerald-800 space-y-3">
              <div className="text-xs text-emerald-300 uppercase tracking-wide">Daily target</div>
              <div className="text-3xl font-bold text-emerald-200 tabular-nums">
                {targetCalories} <span className="text-sm font-normal text-emerald-400/70">kcal/day</span>
              </div>
              <div className="text-xs text-emerald-300/80">
                {goal === "lose" && "500 kcal deficit ≈ 1 lb/week loss"}
                {goal === "maintain" && "Eating at maintenance"}
                {goal === "gain" && "300 kcal surplus ≈ 0.6 lb/week gain"}
              </div>
              <div className="pt-2 border-t border-emerald-800/60 grid grid-cols-3 gap-2 text-xs">
                <Macro label="Protein" value={macros.protein_g} color="#38bdf8" />
                <Macro label="Carbs" value={macros.carbs_g} color="#fbbf24" />
                <Macro label="Fat" value={macros.fat_g} color="#a78bfa" />
              </div>
            </div>

            <div className="text-xs text-slate-500">
              You can change any of these in Settings later.
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
                {error}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer nav */}
      <div className="px-4 pb-6 pt-2 flex gap-2 sticky bottom-0 bg-slate-950">
        {step !== "welcome" && (
          <button
            onClick={() => {
              const order: Step[] = ["welcome", "basics", "activity", "goal", "review"];
              setStep(order[Math.max(0, order.indexOf(step) - 1)]);
            }}
            disabled={saving}
            className="flex-1 py-3 rounded-lg border border-slate-700 text-slate-300 disabled:opacity-50"
          >
            Back
          </button>
        )}
        {step !== "review" ? (
          <button
            onClick={() => {
              const order: Step[] = ["welcome", "basics", "activity", "goal", "review"];
              setStep(order[Math.min(order.length - 1, order.indexOf(step) + 1)]);
            }}
            className="flex-1 py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold"
          >
            {step === "welcome" ? "Get started" : "Continue"}
          </button>
        ) : (
          <button
            onClick={save}
            disabled={saving}
            className="flex-1 py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save & start tracking"}
          </button>
        )}
      </div>

      {step === "welcome" && (
        <div className="px-4 pb-4 text-center">
          <button onClick={onSkip} className="text-xs text-slate-500 underline">
            Skip — use default goals (2000 kcal)
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-2">
      <span className="text-sm text-slate-300 font-medium">{label}</span>
      {children}
    </label>
  );
}

function Macro({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="text-center">
      <div className="inline-block w-2 h-2 rounded-full mb-1" style={{ backgroundColor: color }} />
      <div className="text-emerald-100 font-semibold tabular-nums">{value}g</div>
      <div className="text-[10px] text-emerald-400/70">{label}</div>
    </div>
  );
}
